// import AlertsContext from 'common/contexts/alerts';
import useRouter from 'common/hooks/use-router';
import useItem from 'common/hooks/useItem';
import useList from 'common/hooks/useList';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UsersIcon } from '@heroicons/react/24/outline';
import Header from 'common/components/Header/Header';
import FilteredList from 'common/components/Table/FilteredList';
import AlertsContext from 'common/providers/alerts';
import UserSearch from 'components/search/UserSearch';
import MediatorItem from './MediatorItem';
import { User } from 'types';
import ButtonGroup from 'common/components/ButtonGroup/ButtonGroup';

function Mediators() {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { setAlert } = useContext(AlertsContext);

  const canManageUsers = true;

  const {
    fetchItems,
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<User>('users', {
    defaultFilters: {
      role: {
        name: 'Mediator',
      },
    },
    defaultSort: 'username:ASC',
    populate: ['operator', 'role', 'operator.establishments', 'operator.establishments.department'],
    cachePrefix: 'MediatorUsers',
  });

  useEffect(() => {
    if (error) {
      setAlert(error, 'danger');
    }
  }, [error, setAlert]);

  const { removeItem: deleteUser } = useItem<User>('users', '');

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await deleteUser(id);
        fetchItems();
      } catch (e: any) {
        setAlert(e, 'danger');
        return e;
      }
      return true;
    },
    [removeListItem, fetchItems, deleteUser, setAlert]
  );

  const handleNavigate = (id: any) => {
    navigate(`/mediators/edit/${id}`);
  };

  const styleTop = 'md:w-1/3 xl:flex-1 xl:w-auto';
  const styleBottom = 'md:w-1/3 mt-2 xl:flex-1 xl:w-auto xl:mt-0';

  const columns = useMemo(
    () => [
      {
        Header: t('filters.search'),
        filterName: 'username$containsi',
        columnClasses: styleTop,
        filterLabel: t('filters.search'),
        filterComponent: (props: any) => <UserSearch isFilter {...props} />,
      },
      {
        Header: t('forms.blocked'),
        filterName: 'blocked',
        filterComponent: (props: any) => (
          <ButtonGroup
            className="w-full"
            options={[
              { value: null, label: t('status.all') },
              { value: false, label: t('status.active') },
              { value: true, label: t('status.blocked') },
            ]}
            {...props}
          />
        ),
      },
      {
        Header: t('forms.noProfile'),
        filterName: 'operator][id][$null',
        filterComponent: (props: any) => (
          <ButtonGroup
            className="w-full"
            options={[
              { value: null, label: t('status.all') },
              { value: true, label: t('status.noProfile') },
            ]}
            {...props}
          />
        ),
      },
    ],
    [t]
  );

  return (
    <div className="mx-auto max-w-full py-6 px-6 text-slate-500">
      <Header
        title={t('titlePages.operators')}
        subTitle={t('titleLegends.operators')}
        btnTitle={t('newItem.operator')}
        btnSubtitle={t('newItem.operatorLegend')}
        Icon={UsersIcon}
        addLink="/users/add?role=Mediator"
        addLinkDisabled={!canManageUsers}
        isBeforeFilters
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={MediatorItem}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default Mediators;
